import { ITranslationsCollection } from '@estee/elc-service';
import {
    ICustomValidation,
    IDropdownOption,
    IFieldValidationsConfig,
    IValidatorMessages
} from '../domain/entities/Config';

// This is basically the same thing as defined ELC FormValidator, but accepts custom validator params。
export interface IValidatorObj {
    [key: string]: string | Omit<ICustomValidation, 'validator'>;
}

interface IFieldValidationsOptions extends IFieldValidationsConfig {
    validationConfigs?: (string | ICustomValidation)[];
    errorMessages?: IValidatorMessages;
    translations: ITranslationsCollection;
}

/**
 * A utility to transform field validation configurations to FormValidator compatible validations
 */
export class FieldValidations {
    valueType?: string;
    translations: ITranslationsCollection;
    validations?: IValidatorObj;
    options?: IDropdownOption[];

    constructor(options?: IFieldValidationsOptions) {
        const {
            type: valueType,
            validations: validationConfigs,
            options: fieldOptions,
            translations = {},
            errorMessages = {}
        } = options || {};
        this.valueType = valueType;
        this.options = fieldOptions?.map(({ value, label = '' }) => ({ value: `${value}`, label }));
        this.translations = translations;
        // This will be passed to ELC Field component, empty object ({}) will cause issue but undefined is fine.
        this.validations = validationConfigs?.reduce((validations, validation) => {
            if (typeof validation === 'string') {
                // Validator name without parameters, e.g. 'required', 'min'
                const validator = validation.split(':')[0];

                return {
                    ...validations,
                    [validation]: this.getValidationErrorMessage(validator, errorMessages)
                };
            } else {
                const { validator, ...params } = validation as ICustomValidation;

                return {
                    ...validations,
                    [validator]: {
                        ...params,
                        errorMessage: this.getValidationErrorMessage(validator, errorMessages)
                    }
                };
            }
        }, {});
        if (this.isRequired && fieldOptions) {
            // Due to yup doesn't allow multiple Schema on one field, and yup.oneOf Schema doesn't provide any tests,
            // it's not possible to have 'required' and 'oneOf' Schema at the same time.
            delete this.validations?.required;
            this.validations = {
                oneOf: {
                    values: fieldOptions.map((n) => n.value),
                    required: true,
                    errorMessage: this.getValidationErrorMessage('required', errorMessages)
                }
            };
        }
    }

    private getValidationErrorMessage(validator: string, errorMessages: IValidatorMessages) {
        const transKey = errorMessages[validator] || errorMessages.default;
        const errorMessage = (transKey !== undefined && this.translations[transKey]) || '';

        if (!errorMessage) {
            console.warn(`Error message is missing for ${validator} rule`);
        }

        return errorMessage;
    }

    public get isRequired() {
        return !!this.validations?.hasOwnProperty('required');
    }

    public get maxLength() {
        if (!this.validations) {
            return undefined;
        }
        const maxValidation = Object.keys(this.validations).find(
            (key) => typeof key === 'string' && key.split(':')[0] === 'max'
        );

        return maxValidation ? parseInt(maxValidation.split(':')[1], 10) : undefined;
    }

    // Update validation's error message by registered validator name - without params
    public setErrorMessage = (validatorName: string, errorMessage: string) => {
        if (!this.validations) {
            return;
        }

        const validator = Object.keys(this.validations).find(
            (n) => n.split(':')[0] === validatorName
        );
        if (validator) {
            const validation = this.validations[validator];
            if (typeof validation === 'object') {
                validation.errorMessage = errorMessage;
            } else {
                this.validations = {
                    ...this.validations,
                    [validator]: errorMessage
                };
            }
        }
    };
}
